import type { ListTrainsResponse } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSyncConfigV1Service, serviceName } from '../service';
import { ListTrainsIO, ServiceQueries } from '../types';
import {
  UseSyncConfigV1InfiniteQueryEndpointArgs,
  useSyncConfigV1InfiniteQuery,
} from './use-sync-config-v1-infinite-query';

type ListTrainsPageParam = string;
type ListTrainsWireResponse = ListTrainsResponse & {
  next_page_token?: string;
};
/**
 * A hook that returns an infinite query for the `ListTrains` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListTrainsQuery = <E = Error, D = ListTrainsIO['output']>({
  options,
  request,
  ...args
}: UseSyncConfigV1InfiniteQueryEndpointArgs<'ListTrains', E, D, ListTrainsPageParam>) =>
  useSyncConfigV1InfiniteQuery<'ListTrains', E, D, ListTrainsPageParam>({
    endpointName: 'ListTrains',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: ListTrainsIO['input'] = {
        ...request,
        pageToken: pageParam ?? request.pageToken,
      };
      return requestWithPageParams;
    },
    options: {
      ...options,
      getNextPageParam: (lastPage) => {
        const response = lastPage as ListTrainsWireResponse;
        const nextPageToken = response.nextPageToken ?? response.next_page_token;

        return nextPageToken || undefined;
      },
    },
  });

/**
 * A hook that returns a function to fetch the query for the `ListTrains` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListTrains = () => {
  const queryClient = useQueryClient();

  const fetchListTrains = async <E = Error, D = ListTrainsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSyncConfigV1InfiniteQueryEndpointArgs<'ListTrains', E, D>, 'options'> & {
    options: LimitedSchemaFetchQueryOptions<
      ListTrainsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListTrains'>>
    >;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        ListTrainsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListTrains'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListTrains'>({
          serviceName,
          endpointName: 'ListTrains',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSyncConfigV1Service.ListTrains(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError ?? ({} as D);
      }
      throw error;
    }
  };

  return fetchListTrains;
};
